<template>
  <div class="container-fluid px-4 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="fw-bold mb-0">중계서버 API 테스트</h5>
    </div>

    <!-- 탭 네비게이션 -->
    <ul class="nav nav-tabs mb-3" role="tablist">
      <li class="nav-item" role="presentation">
        <button 
          :class="['nav-link', { active: activeTab === 'seasonTicket' }]"
          @click="activeTab = 'seasonTicket'"
          type="button"
        >
          정기권 조회
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button 
          :class="['nav-link', { active: activeTab === 'changes' }]"
          @click="activeTab = 'changes'"
          type="button"
        >
          변경이력 조회
        </button>
      </li>
    </ul>

    <!-- 정기권 조회 탭 -->
    <div v-if="activeTab === 'seasonTicket'">
      <div class="card shadow-sm mb-3">
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <div class="col-md-3">
              <label class="form-label small fw-bold mb-1">AreaSeq</label>
              <input v-model.number="areaSeq" type="number" class="form-control form-control-sm" placeholder="예: 1" />
            </div>
            <div class="col-md-4">
              <label class="form-label small fw-bold mb-1">API Key</label>
              <input v-model="apiKey" type="text" class="form-control form-control-sm" placeholder="x-api-key 값 입력" />
            </div>
            <div class="col-md-3">
              <button class="btn btn-primary btn-sm" @click="fetchSeasonTicket" :disabled="loading">
                {{ loading ? '조회 중...' : '테스트' }}
              </button>
            </div>
            <div class="col-md-2">
              <label class="form-label small fw-bold mb-1">요청 URL</label>
              <div class="form-control form-control-sm bg-light text-monospace small" style="overflow:auto;height:38px;">
                {{ apiUrl }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger py-2 mb-3">{{ error }}</div>

      <div class="card shadow-sm">
        <div class="card-body p-0">
          <div class="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-light">
            <strong class="small">응답 JSON</strong>
            <button v-if="responseText" class="btn btn-outline-secondary btn-xs" @click="copyJson">복사</button>
          </div>
          <pre class="mb-0 json-viewer">{{ responseText || '아직 응답이 없습니다.' }}</pre>
        </div>
      </div>

      <div class="card shadow-sm mt-4">
        <div class="card-body p-0">
          <div class="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-light">
            <strong class="small">중계서버 request log</strong>
            <button class="btn btn-outline-secondary btn-xs" @click="fetchRequestLogs">새로고침</button>
          </div>

          <div v-if="requestLogs.length === 0" class="p-3 small text-muted">
            로그가 없습니다.
          </div>

          <div v-else class="table-responsive">
            <table class="table table-sm table-hover mb-0 align-middle">
              <thead class="table-light">
                <tr>
                  <th style="width: 80px;">LogSeq</th>
                  <th style="width: 90px;">AreaSeq</th>
                  <th style="width: 200px;">EndPoint</th>
                  <th>QueryString</th>
                  <th style="width: 120px;">ClientIp</th>
                  <th style="width: 100px;">Result</th>
                  <th style="width: 180px;">CreateDate</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in requestLogs" :key="log.LogSeq">
                  <td>{{ log.LogSeq }}</td>
                  <td>{{ log.AreaSeq ?? '-' }}</td>
                  <td>{{ log.EndPoint || '-' }}</td>
                  <td class="text-break small">{{ log.QueryString || '-' }}</td>
                  <td>{{ log.ClientIp || '-' }}</td>
                  <td>
                    <span :class="['badge', log.Result === 'SUCCESS' ? 'bg-success' : 'bg-danger']">
                      {{ log.Result || '-' }}
                    </span>
                  </td>
                  <td>{{ formatDate(log.CreateDate) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- 변경이력 조회 탭 -->
    <div v-if="activeTab === 'changes'">
      <div class="card shadow-sm mb-3">
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <div class="col-md-3">
              <label class="form-label small fw-bold mb-1">AreaSeq *</label>
              <input v-model.number="changesAreaSeq" type="number" class="form-control form-control-sm" placeholder="예: 1" />
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-bold mb-1">LastChangeSeq</label>
              <input v-model.number="lastChangeSeq" type="number" class="form-control form-control-sm" placeholder="기본: 0" />
            </div>
            <div class="col-md-2">
              <button class="btn btn-primary btn-sm" @click="fetchChanges" :disabled="changesLoading || !changesAreaSeq">
                {{ changesLoading ? '조회 중...' : '조회' }}
              </button>
            </div>
            <div class="col-md-4">
              <label class="form-label small fw-bold mb-1">요청 URL</label>
              <div class="form-control form-control-sm bg-light text-monospace small" style="overflow:auto;height:38px;">
                {{ changesApiUrl }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="changesError" class="alert alert-danger py-2 mb-3">{{ changesError }}</div>

      <div v-if="changesData" class="alert alert-info py-2 mb-3">
        <strong>마지막 ChangeSeq:</strong> {{ changesData.lastChangeSeq }} 
        <strong class="ms-3">변경건수:</strong> {{ changesData.changes.length }}
      </div>

      <div class="card shadow-sm">
        <div class="card-body p-0">
          <div class="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-light">
            <strong class="small">응답 JSON</strong>
            <button v-if="changesResponseText" class="btn btn-outline-secondary btn-xs" @click="copyChangesJson">복사</button>
          </div>
          <pre class="mb-0 json-viewer">{{ changesResponseText || '아직 응답이 없습니다.' }}</pre>
        </div>
      </div>

      <div v-if="changesData && changesData.changes.length > 0" class="card shadow-sm mt-4">
        <div class="card-body p-0">
          <div class="px-3 py-2 border-bottom bg-light">
            <strong class="small">변경 이력 목록</strong>
          </div>
          <div class="table-responsive">
            <table class="table table-sm table-hover mb-0 align-middle">
              <thead class="table-light">
                <tr>
                  <th style="width: 80px;">ChangeSeq</th>
                  <th style="width: 100px;">차량번호</th>
                  <th style="width: 100px;">시작일</th>
                  <th style="width: 100px;">종료일</th>
                  <th style="width: 80px;">변경타입</th>
                  <th style="width: 200px;">변경일시</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="change in changesData.changes" :key="change.changeSeq">
                  <td><code>{{ change.changeSeq }}</code></td>
                  <td>{{ change.carNumber || '-' }}</td>
                  <td>{{ change.startDate }}</td>
                  <td>{{ change.endDate }}</td>
                  <td>
                    <span :class="['badge', getChangeBadgeClass(change.changeType)]">
                      {{ change.changeType }}
                    </span>
                  </td>
                  <td class="small">{{ change.changedDate }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-else-if="changesData && changesData.changes.length === 0" class="alert alert-info mt-4">
        변경된 정기권이 없습니다.
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AdminApiTestView',
  data() {
    return {
      activeTab: 'seasonTicket',
      // 정기권 조회 탭
      areaSeq: '',
      apiKey: '',
      loading: false,
      error: '',
      responseText: '',
      requestLogs: [],
      // 변경이력 조회 탭
      changesAreaSeq: '',
      lastChangeSeq: 0,
      changesLoading: false,
      changesError: '',
      changesResponseText: '',
      changesData: null
    }
  },
  computed: {
    apiUrl() {
      const base = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
      const qs = this.areaSeq !== '' && this.areaSeq !== null ? `?areaSeq=${encodeURIComponent(this.areaSeq)}` : ''
      return `${base}/api/v1/season-ticket${qs}`
    },
    changesApiUrl() {
      const base = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
      const params = []
      if (this.changesAreaSeq) params.push(`areaSeq=${this.changesAreaSeq}`)
      if (this.lastChangeSeq !== 0) params.push(`lastChangeSeq=${this.lastChangeSeq}`)
      const qs = params.length > 0 ? `?${params.join('&')}` : ''
      return `${base}/api/active-parking-pass/changes${qs}`
    }
  },
  mounted() {
    this.fetchRequestLogs()
  },
  methods: {
    async fetchSeasonTicket() {
      this.loading = true
      this.error = ''
      this.responseText = ''

      try {
        const params = {}
        if (this.areaSeq !== '' && this.areaSeq !== null) params.areaSeq = this.areaSeq

        const res = await axios.get(`${process.env.VUE_APP_API_BASE || ''}/api/v1/season-ticket`, {
          params,
          headers: {
            'x-api-key': this.apiKey || ''
          }
        })
        this.responseText = JSON.stringify(res.data, null, 2)
      } catch (e) {
        const msg = e.response?.data?.message || e.message || 'API 호출에 실패했습니다.'
        this.error = msg
        this.responseText = JSON.stringify(e.response?.data || {}, null, 2)
      } finally {
        this.loading = false
        await this.fetchRequestLogs()
      }
    },
    async fetchChanges() {
      if (!this.changesAreaSeq) {
        this.changesError = 'AreaSeq를 입력해주세요.'
        return
      }

      this.changesLoading = true
      this.changesError = ''
      this.changesResponseText = ''
      this.changesData = null

      try {
        const params = {
          areaSeq: this.changesAreaSeq,
          lastChangeSeq: this.lastChangeSeq || 0
        }

        const res = await axios.get(`${process.env.VUE_APP_API_BASE || ''}/api/active-parking-pass/changes`, {
          params
        })

        this.changesData = res.data
        this.changesResponseText = JSON.stringify(res.data, null, 2)
      } catch (e) {
        const msg = e.response?.data?.message || e.message || 'API 호출에 실패했습니다.'
        this.changesError = msg
        this.changesResponseText = JSON.stringify(e.response?.data || {}, null, 2)
      } finally {
        this.changesLoading = false
      }
    },
    async fetchRequestLogs() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE || ''}/api/admin/open-api-request-log`)
        this.requestLogs = Array.isArray(res.data) ? res.data : []
      } catch (e) {
        console.error('request log fetch failed:', e)
        this.requestLogs = []
      }
    },
    formatDate(value) {
      if (!value) return '-'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
    },
    getChangeBadgeClass(changeType) {
      const classes = {
        'INSERT': 'bg-success',
        'UPDATE': 'bg-info',
        'DELETE': 'bg-danger'
      }
      return classes[changeType] || 'bg-secondary'
    },
    async copyJson() {
      try {
        await navigator.clipboard.writeText(this.responseText)
        alert('JSON이 복사되었습니다.')
      } catch (e) {
        console.error(e)
        alert('복사에 실패했습니다.')
      }
    },
    async copyChangesJson() {
      try {
        await navigator.clipboard.writeText(this.changesResponseText)
        alert('JSON이 복사되었습니다.')
      } catch (e) {
        console.error(e)
        alert('복사에 실패했습니다.')
      }
    }
  }
}
</script>

<style scoped>
.json-viewer {
  background: #111827;
  color: #e5e7eb;
  padding: 1rem;
  margin: 0;
  min-height: 220px;
  max-height: 70vh;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.8rem;
  line-height: 1.6;
  font-family: 'Consolas', 'Monaco', monospace;
}

.btn-xs {
  padding: 0.2rem 0.5rem;
  font-size: 0.75rem;
}
</style>
